import {configOption, configSubObject, KahunConfigurationOptionType} from "../lib/annotations";
import { SupportedLanguages } from "./supportedLanguages";
import {DialogueOptions} from "./dialogueOptions";
import {StandaloneOptions} from "./standaloneOptions";
import {ConversionTrackingOptions} from "./conversionTrackingOptions";

export enum SummarySectionType {
  Assessment = "assessment",
  Dd = "dd",
  Hpi = "hpi",
  Imaging = "imaging",
  Labs = "labs",
  Pe = "pe",
  Plan = "plan",
  Referral = "referral",
  Ros = "ros",
  Suggested = "suggested",
  TriageAdvice = "triage_advice",
}

export enum ClinicCtaActionType {
  Url = "url",
  Component = "component",
}

export interface ClinicCta {
  id: string;
  // Enum (usually WhatsNextOptionId, but not necessarily) in case of component
  // or url in case of external option while containing {OP} that would be translated
  // to any valid filed from clinicSetting
  action: string;
  actionType: ClinicCtaActionType;
}

export class ClinicGeneralOptions {
  @configOption({
    description: "Inherit configuration settings from a base clinic",
    displayType: KahunConfigurationOptionType.Clinic,
  })
  basedOnClinic?: string;

  @configOption({
    description: 'Set clinic as a "demo clinic" to hide the sidebar and block the settings screen',
    displayType: KahunConfigurationOptionType.Boolean,
  })
  demoMode?: boolean;

  @configOption({
    displayName: "Default Chat Language",
    description:
      'What language to show in the chat and in invite messages if no "lang=xx" was provided. default = en',
    options: SupportedLanguages,
    displayType: KahunConfigurationOptionType.Choice,
  })
  defaultLocale?: SupportedLanguages = SupportedLanguages.English_US;

  @configOption({
    displayName: "Clinic Management language",
    description:
      "What language to use for the clinic manage web site (default - same as chat language)",
    options: SupportedLanguages,
    displayType: KahunConfigurationOptionType.Choice,
  })
  clinicManageLocale?: SupportedLanguages = null;

  @configOption({
    description:
      "From what list of languages we allow users to select their locale (some may want limited list and some would like all the languages)",
    options: SupportedLanguages,
    displayType: KahunConfigurationOptionType.String,
  })
  supportedLanguages?: SupportedLanguages[] = [
    SupportedLanguages.English_US,
    SupportedLanguages.English_GB,
    SupportedLanguages.Spanish,
    SupportedLanguages.Hebrew,
  ];

  @configSubObject({
    displayName: "Kahun Dialogue configuration",
    description: "Enable Kahun Dialogue and set the App ID",
  })
  dialogue?: DialogueOptions;

  @configSubObject({
    displayName: "Standalone configuration",
    description: "Configure standalone (clinic manage) options",
  })
  standalone?: StandaloneOptions;

  @configSubObject({
    displayName: "Conversion Tracking",
    description: "Google Analytics, Facebook Pixel, etc.",
  })
  conversionTracking?: ConversionTrackingOptions;

  @configOption({
    description:
      'Should we add a "More Info" button in lists of clusters that will lead to https://partners.kahun.com/moreinfo lambda',
  })
  linkToExternalDisease?: boolean;

  @configOption({
    description: "Will the conversation ending lead to a thank you page",
  })
  showThankYouPage?: boolean;

  @configOption({ description: "Will the conversation lead to a summary page" })
  showSummary?: boolean;

  @configOption({
    description:
      "Will we show a link to 'Show Me The Results' in summary page? If set to true a wave area with a button will appear in the bottom of the screen with a link to the results page",
  })
  linkSummaryToResults?: boolean = true;

  @configOption({
    description:
      "Whether we want to display the list of top clusters in results page",
  })
  hideDD?: boolean = false;

  @configOption({
    description:
      "Whether we use kahun for physical intake in clinics (where users will get a number at the end that will lead" +
      " to the patient card) and patients will be listed in patient dashboard",
  })
  clinicMode?: boolean = false;

  @configOption({})
  patientSummarySections: SummarySectionType[] = [];

  @configOption({
    description: "Which sections to show in the summary, empty means show all.",
  })
  agentSummarySections?: SummarySectionType[] = [];

  @configOption({
    description:
      "In private mode we don't report detailed events on users, currently in the client side. For example we will report that the user answered a question, but will not report what is the answer ",
  })
  privateMode?: boolean = false;

  @configOption({
    description:
      "After which % progress we show the skip to results button. This should probably be replaced with concrete logic, like if there is a DD.",
    displayType: KahunConfigurationOptionType.Int,
  })
  minimumProgressToSkip?: number = 20;

  @configOption({
    description:
      'An array of CTA configurations that currently look like that: { id: string; action: string/enum, actionType: "component" or "url"} action can have hotwords like {clinicId} {caseId} {partnerId} or {apiUrl}, currently the only cta id we support is askDoctorAsButton feature flag key, which ads an "ASK A DOCTOR" button and here hijackts it\'s action - we use this for media orbis',
    displayType: KahunConfigurationOptionType.JSON,
  })
  cta?: ClinicCta[];

  @configOption({
    description: "This clinic is vim integration",
    displayType: KahunConfigurationOptionType.Boolean,
  })
  vim?: boolean;
}
